import React, { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  useMediaQuery,
  useTheme,
  Divider,
} from "@mui/material";
import {
  LocationOn,
  VerifiedUser,
  Notifications,
  Assignment,
  People,
  Map,
  Star,
  CheckCircle,
  TrendingUp,
  AccountBalanceWallet,
  Language,
  Security,
  Payment,
  Speed,
  SupportAgent,
  ArrowForward,
  PlayArrow,
  EmojiEvents,
  ThumbUp,
  Groups,
  WorkOutline,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import Recaptcha from "../components/Common/Recaptcha";
import { YandexIcon, GoogleIcon } from "../components/Common/OAuthIcons";
import "./LandingModern.css";

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const LandingModern = () => {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [registerError, setRegisterError] = useState("");
  const [registrationIntent, setRegistrationIntent] = useState<
    "performer" | "customer" | null
  >(null);
  const registerFormRef = useRef<HTMLFormElement>(null);
  const loginFormRef = useRef<HTMLFormElement>(null);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login, user, isAuthenticated } = useAuth();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaKeyRef = useRef(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 },
    );

    const elements = document.querySelectorAll(".animate-on-scroll");
    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const openLoginModal = () => {
    setIsLoginModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLoginModal = () => {
    setIsLoginModalOpen(false);
    document.body.style.overflow = "";
    setLoginError("");
  };

  const openRegisterModal = () => {
    setIsRegisterModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeRegisterModal = () => {
    setIsRegisterModalOpen(false);
    document.body.style.overflow = "";
    setRegisterError("");
    setCaptchaToken(null);
    captchaKeyRef.current++;
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError("");

    if (!loginFormRef.current) {
      setLoginError("Login form not found");
      return;
    }

    const form = loginFormRef.current;
    const formData = new FormData(form);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    try {
      const result = await login(email, password);
      if (result.success) {
        closeLoginModal();
        navigate("/home");
      } else {
        setLoginError(result.error || t("invalid_credentials"));
      }
    } catch (error) {
      console.error("Login error:", error);
      setLoginError(t("invalid_credentials"));
    }
  };

  const handleYandexLogin = () => {
    // Redirect to Yandex OAuth endpoint
    const redirectUrl = encodeURIComponent(window.location.origin + "/home");
    window.location.href = `/api/v1/auth/yandex?redirect=${redirectUrl}`;
  };

  const handleGoogleLogin = () => {
    // Redirect to Google OAuth endpoint
    const redirectUrl = encodeURIComponent(window.location.origin + "/home");
    window.location.href = `/api/v1/auth/google?redirect=${redirectUrl}`;
  };

  const handleCaptchaVerify = (token: string) => {
    setCaptchaToken(token);
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterError("");

    if (!registerFormRef.current) {
      setRegisterError("Registration form not found");
      return;
    }

    if (!captchaToken) {
      setRegisterError("Please complete the captcha");
      return;
    }

    const form = registerFormRef.current;
    const formData = new FormData(form);
    const name = formData.get("name") as string;
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const confirmPassword = formData.get("confirmPassword") as string;
    const roleSelect = form.querySelector("select") as HTMLSelectElement;
    const role = roleSelect ? roleSelect.value : "client";

    if (password !== confirmPassword) {
      setRegisterError("Passwords do not match");
      return;
    }

    try {
      const response = await fetch("/api/v1/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          password,
          role,
          captcha_token: captchaToken,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        closeRegisterModal();
        navigate("/home");
      } else {
        setRegisterError(data.error || "Registration error");
      }
    } catch (error) {
      console.error("Registration error:", error);
      setRegisterError("Network error");
    }
  };

  const features: Feature[] = [
    {
      icon: <LocationOn sx={{ fontSize: 40 }} />,
      title: "Geolocation",
      description:
        "Find performers and orders near you based on geolocation",
    },
    {
      icon: <Payment sx={{ fontSize: 40 }} />,
      title: "Secure payments",
      description:
        "Secure transactions with funds held until order completion",
    },
    {
      icon: <VerifiedUser sx={{ fontSize: 40 }} />,
      title: "Verification",
      description: "Verified performers with ratings and reviews",
    },
    {
      icon: <Notifications sx={{ fontSize: 40 }} />,
      title: "Notifications",
      description:
        "Instant notifications about new orders and status changes",
    },
    {
      icon: <Speed sx={{ fontSize: 40 }} />,
      title: "Fast execution",
      description:
        "Nearby performers respond in minutes — from response to start of work in just hours",
    },
    {
      icon: <Star sx={{ fontSize: 40 }} />,
      title: "Ratings",
      description: "Rating and review system for quality control",
    },
    {
      icon: <Map sx={{ fontSize: 40 }} />,
      title: "Order map",
      description:
        "Real-time order visualization on an interactive map",
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      title: "Analytics",
      description: "Detailed statistics and reports for performance analysis",
    },
  ];

  const steps = [
    {
      number: "1",
      title: "Create an order",
      description: "Describe the task, specify address and amount",
    },
    {
      number: "2",
      title: "Choose a performer",
      description: "Get responses and choose the best one",
    },
    {
      number: "3",
      title: "Track progress",
      description: "Monitor progress in real time",
    },
    {
      number: "4",
      title: "Pay for the result",
      description: "Secure payment after work completion",
    },
  ];

  return (
    <div className="landing-page">
      {/* Header */}
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        sx={{ backdropFilter: "blur(10px)", bgcolor: "rgba(255,255,255,0.9)" }}
      >
        <Toolbar>
          <Box
            component="img"
            src="/logo_dp.webp"
            alt="DispatcherPro"
            sx={{ height: 40, width: 40, mr: 1.5 }}
          />
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, fontWeight: 800, color: "primary.main" }}
          >
            DispatcherPro
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={i18n.language}
                onChange={(e) => i18n.changeLanguage(e.target.value)}
                sx={{
                  bgcolor: "rgba(99, 102, 241, 0.15)",
                  borderRadius: "50px",
                  "& fieldset": { border: "none" },
                  "& .MuiSelect-select": {
                    fontWeight: 600,
                    color: "primary.main",
                  },
                }}
              >
                <MenuItem value="ru">🇷🇺 Русский</MenuItem>
                <MenuItem value="en">🇬🇧 English</MenuItem>
                <MenuItem value="kk">🇰🇿 Қазақша</MenuItem>
                <MenuItem value="ky">🇰🇬 Кыргызча</MenuItem>
                <MenuItem value="tg">🇹🇯 Тоҷикӣ</MenuItem>
                <MenuItem value="uz">🇺🇿 O'zbekcha</MenuItem>
              </Select>
            </FormControl>

            {isAuthenticated ? (
              <Button
                variant="contained"
                onClick={() => navigate("/home")}
                sx={{
                  borderRadius: "50px",
                  px: 3,
                  background:
                    "linear-gradient(135deg, #667EEA 0%, #764BA2 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #5568D3 0%, #63408E 100%)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                {t("home")}
              </Button>
            ) : (
              <>
                <Button
                  onClick={openLoginModal}
                  sx={{
                    borderRadius: "50px",
                    px: 3,
                    color: "primary.main",
                    fontWeight: 600,
                    "&:hover": { bgcolor: "rgba(99, 102, 241, 0.1)" },
                  }}
                >
                  {t("login")}
                </Button>
                <Button
                  variant="contained"
                  onClick={openRegisterModal}
                  sx={{
                    borderRadius: "50px",
                    px: 3,
                    background:
                      "linear-gradient(135deg, #667EEA 0%, #764BA2 100%)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #5568D3 0%, #63408E 100%)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  {t("register")}
                </Button>
              </>
            )}
            <Button
              onClick={() => navigate("/for-brigades")}
              sx={{
                borderRadius: "50px", px: 2, color: "primary.main",
                fontWeight: 500, fontSize: 14,
                "&:hover": { bgcolor: "rgba(99, 102, 241, 0.1)" },
              }}
            >
              For brigades
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-floating"></div>
        <div className="hero-floating"></div>
        <div className="hero-floating"></div>

        <div className="hero-content">
          <div className="hero-badge">{t("landing_badge")}</div>

          <h1 className="hero-title">
            {t("landing_title")}
            <br />
            <span
              style={{
                background: "linear-gradient(90deg, #FFD700, #FFA500)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {t("landing_title_end")}
            </span>
          </h1>

          <p className="hero-subtitle">{t("landing_subtitle")}</p>

          <div className="hero-buttons">
            <button
              className="btn-primary"
              onClick={() => {
                if (isAuthenticated) {
                  navigate("/orders/new");
                } else {
                  setRegistrationIntent("customer");
                  openRegisterModal();
                }
              }}
            >
              {t("landing_btn_order")} <ArrowForward sx={{ ml: 1 }} />
            </button>
            <button
              className="btn-secondary"
              onClick={() => {
                setRegistrationIntent("performer");
                openRegisterModal();
              }}
            >
              {t("landing_btn_performer")}
            </button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-container animate-on-scroll">
          <div className="stat-item">
            <div className="stat-icon">
              <Groups />
            </div>
            <div className="stat-number">1000+</div>
            <div className="stat-label">{t("landing_stats_users")}</div>
          </div>
          <div className="stat-item">
            <div className="stat-icon">
              <Assignment />
            </div>
            <div className="stat-number">5000+</div>
            <div className="stat-label">{t("landing_stats_orders")}</div>
          </div>
          <div className="stat-item">
            <div className="stat-icon">
              <Star />
            </div>
            <div className="stat-number">4.8</div>
            <div className="stat-label">{t("landing_stats_rating")}</div>
          </div>
          <div className="stat-item">
            <div className="stat-icon">
              <Speed />
            </div>
            <div className="stat-number">15 min</div>
            <div className="stat-label">Average response time</div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="section-header animate-on-scroll">
          <div className="section-badge">Features</div>
          <h2 className="section-title">{t("landing_why_title")}</h2>
          <p className="section-subtitle">
            We built a platform that makes interaction between
            customers and performers simple, safe, and efficient
          </p>
        </div>

        <div className="features-grid">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`feature-card animate-on-scroll delay-${(index % 4) + 1}`}
            >
              <div className="feature-icon">{feature.icon}</div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works">
        <div className="section-header animate-on-scroll">
          <div className="section-badge">Process</div>
          <h2 className="section-title">{t("landing_how_title")}</h2>
          <p className="section-subtitle">
            Just 4 simple steps from creating an order to getting results
          </p>
        </div>

        <div className="steps-container">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`step-item animate-on-scroll delay-${index + 1}`}
            >
              <div className="step-number">{step.number}</div>
              <h3 className="step-title">{step.title}</h3>
              <p className="step-description">{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content animate-on-scroll">
          <h2 className="cta-title">{t("landing_cta_title")}</h2>
          <p className="cta-subtitle">
            Join thousands of users who have already found their
            ideal performers or received new orders
          </p>
          <div className="cta-buttons">
            <button
              className="btn-primary"
              onClick={() => {
                if (isAuthenticated) {
                  navigate("/orders/new");
                } else {
                  openRegisterModal();
                }
              }}
            >
              Create account <PlayArrow sx={{ ml: 1 }} />
            </button>
            <button className="btn-secondary" onClick={() => navigate("/home")}>
              Learn more
            </button>
          </div>
        </div>
      </section>

      {/* Mobile Apps Section */}
      <section className="mobile-apps-section animate-on-scroll" style={{ padding: '60px 20px', textAlign: 'center', backgroundColor: 'rgba(99, 102, 241, 0.03)' }}>
        <div className="section-header" style={{ marginBottom: '40px' }}>
          <div className="section-badge">Mobile</div>
          <h2 className="section-title">{t("mobile_apps_title")}</h2>
          <p className="section-subtitle">{t("mobile_apps_description")}</p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
          <a 
            href="https://console.rustore.ru/apps/2063742094" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              textDecoration: 'none', 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: '12px', 
              padding: '20px', 
              borderRadius: '16px', 
              backgroundColor: '#fff', 
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              transition: 'transform 0.2s',
              minWidth: '200px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <div style={{ width: 64, height: 64, borderRadius: '12px', backgroundColor: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
              <SupportAgent sx={{ fontSize: 32 }} />
            </div>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1f2937' }}>{t("app_dispatcher")}</Typography>
            <Button variant="contained" size="small" sx={{ borderRadius: '50px', textTransform: 'none' }}>
              {t("download_rustore")}
            </Button>
          </a>
          <a 
            href="https://console.rustore.ru/apps/2063743567" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              textDecoration: 'none', 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: '12px', 
              padding: '20px', 
              borderRadius: '16px', 
              backgroundColor: '#fff', 
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              transition: 'transform 0.2s',
              minWidth: '200px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <div style={{ width: 64, height: 64, borderRadius: '12px', backgroundColor: '#fbbf24', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
              <WorkOutline sx={{ fontSize: 32 }} />
            </div>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1f2937' }}>{t("app_worker")}</Typography>
            <Button variant="contained" size="small" sx={{ borderRadius: '50px', textTransform: 'none' }}>
              {t("download_rustore")}
            </Button>
          </a>
        </div>
      </section>


      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3>{t("landing_footer_title")}</h3>
            <p>{t("landing_subtitle")}</p>
          </div>
          <div className="footer-section">
            <h3>{t("landing_footer_nav")}</h3>
            <p>
              <a href="/home">{t("landing_footer_home")}</a>
            </p>
            <p>
              <a href="/orders/new">{t("landing_footer_create")}</a>
            </p>
            <p>
              <a href="/profile">{t("landing_footer_profile")}</a>
            </p>
          </div>
          <div className="footer-section">
            <h3>{t("landing_footer_contacts")}</h3>
            <p>
              <i className="fas fa-envelope me-1" /> Email:
              pomogayonline@gmail.com
            </p>
            <p>
                            <i className="fas fa-phone me-1" /> Phone: {" "}
              <a
                href="tel:+79264602001"
                style={{ color: "inherit", textDecoration: "none" }}
              >
                +7 (926) 460-20-01
              </a>
            </p>
            <p>
              <i className="fas fa-file-alt me-1" /> TIN: 230902727737
            </p>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2026 DispatcherPro. {t("landing_footer_copyright")}</p>
        </div>
      </footer>

      {/* Login Modal */}
      <Dialog
        open={isLoginModalOpen}
        onClose={closeLoginModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, textAlign: "center" }}>
          Sign in
        </DialogTitle>
        <DialogContent>
          {loginError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {loginError}
            </Alert>
          )}
          <form onSubmit={handleLoginSubmit} ref={loginFormRef}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              margin="normal"
              required
            />
          </form>

          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="textSecondary">
              or
            </Typography>
          </Divider>

          <Button
            fullWidth
            variant="outlined"
            onClick={handleYandexLogin}
            startIcon={<YandexIcon />}
            sx={{
              borderColor: "#FC3F1D",
              color: "#FC3F1D",
              "&:hover": {
                borderColor: "#d32f2f",
                backgroundColor: "rgba(252,63,29,0.04)",
              },
            }}
          >
            Sign in with Yandex
          </Button>

          <Button
            fullWidth
            variant="outlined"
            onClick={handleGoogleLogin}
            sx={{ mt: 1 }}
            startIcon={<GoogleIcon />}
          >
            Sign in with Google
          </Button>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button onClick={closeLoginModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleLoginSubmit}
            sx={{ borderRadius: "50px", px: 4 }}
          >
            Sign in
          </Button>
        </DialogActions>
      </Dialog>

      {/* Register Modal */}
      <Dialog
        open={isRegisterModalOpen}
        onClose={closeRegisterModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, textAlign: "center" }}>
          Registration
        </DialogTitle>
        <DialogContent>
          {registerError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {registerError}
            </Alert>
          )}
          <form onSubmit={handleRegisterSubmit} ref={registerFormRef}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Confirm password"
              name="confirmPassword"
              type="password"
              margin="normal"
              required
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select
                defaultValue={registrationIntent || "client"}
                label="Role"
                name="role"
              >
                <MenuItem value="client">Customer</MenuItem>
                <MenuItem value="performer">Performer</MenuItem>
              </Select>
            </FormControl>
            <Recaptcha
              key={captchaKeyRef.current}
              onVerify={handleCaptchaVerify}
            />
          </form>

          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="textSecondary">
              or
            </Typography>
          </Divider>

          <Button
            fullWidth
            variant="outlined"
            onClick={handleYandexLogin}
            startIcon={<YandexIcon />}
            sx={{
              borderColor: "#FC3F1D",
              color: "#FC3F1D",
              "&:hover": {
                borderColor: "#d32f2f",
                backgroundColor: "rgba(252,63,29,0.04)",
              },
            }}
          >
            Sign up with Yandex
          </Button>

          <Button
            fullWidth
            variant="outlined"
            onClick={handleGoogleLogin}
            sx={{ mt: 1 }}
            startIcon={<GoogleIcon />}
          >
            Sign up with Google
          </Button>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button onClick={closeRegisterModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRegisterSubmit}
            sx={{ borderRadius: "50px", px: 4 }}
          >
            Sign up
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LandingModern;
